import { useState } from "react";
import * as XLSX from "xlsx";
import { getData } from "../services/apiService";
import { toast } from "react-toastify";

const useFileDownload = ({ url, defaultFilename = "Report" }) => {
    const [loading, setLoading] = useState(false);
    const [fileError, setFileError] = useState(null);

    const downloadFile = async (selectedFileType, customFilename = null) => {
        if (!selectedFileType) return;

        setLoading(true);
        setFileError(null);

        try {
            const responseType = ["excel", "pdf", "csv"].includes(selectedFileType) ? "blob" : "json";
            const Apiurl = `${url}${selectedFileType}`;
            const response = await getData(Apiurl, responseType);

            if (!response) {
                throw new Error("No data received from API");
            }

            let blob;
            const baseFilename = customFilename || defaultFilename;
            const fileExtension = selectedFileType === "excel" ? "xlsx" : selectedFileType;
            const fileName = `${baseFilename}.${fileExtension}`;

            switch (selectedFileType) {
                case "pdf":
                case "csv":
                    blob = response.data || response;
                    break;

                case "excel":
                    if (response instanceof Blob || response.data instanceof Blob) {
                        blob = response.data || response;
                    } else {
                        let jsonData;
                        if (Array.isArray(response)) {
                            jsonData = response;
                        } else if (response.data) {
                            if (typeof response.data === "string") {
                                jsonData = JSON.parse(response.data);
                            } else {
                                jsonData = response.data;
                            }
                        } else {
                            jsonData = response;
                        }

                        const worksheet = XLSX.utils.json_to_sheet(jsonData);
                        const workbook = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

                        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
                        blob = new Blob([excelBuffer], {
                            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                        });
                    }
                    break;

                default:
                    throw new Error("Invalid file format selected");
            }

            if (!blob) {
                throw new Error("No data available for download");
            }

            // Trigger File Download
            const objectURL = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = objectURL;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => {
                URL.revokeObjectURL(objectURL);
            }, 100);

            // ✅ Show success toast with better formatting
            toast.success(`✅ ${fileName} downloaded successfully!`, {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } catch (err) {
            const errorMessage = err.message || "Failed to download file.";
            setFileError(errorMessage);
            console.error("Download error:", err);

            // ❌ Show error toast with more details
            toast.error(`❌ Download failed: ${errorMessage}`, {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    return { downloadFile, loading, fileError };
};

export default useFileDownload;